'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { AnimatePresence } from 'framer-motion';
import Tag from '../ui/Tag';
import MuxPlayer from '@mux/mux-player-react';
import JournalModal from '../common/journalDetail';

interface Button {
  button_url?: string;
  button_text?: string;
  open_link_in_new_tab?: boolean;
}

interface Media {
  url: string;
  desktop_media?: Media[];
}

interface Article {
  id?: string;
  slug?: string;
  documentId?: string;
  awards_text?: string;
  date?: string;
  media?: {
    desktop_media?: Media[];
    mobile_media?: Media[];
    enable_mux_video?: boolean;
    mux_video?: {
      playback_id?: string;
    };
  };
  button?: Button;
  name?: string;
  description?: any; // keep as any if unknown structure
  journal?: any;
}

interface Data {
  journals?: Article[];
  heading: string;
  __typename: string;
  padding: {
    mobile_top_padding: any;
    mobile_bottom_padding: any;
    desktop_top_padding: any;
    desktop_bottom_padding: any;
  };
}

interface Journal {
  id: string;
  name: string;
  awards_text?: string;
  date?: string;
  button: {
    button_text: string;
    button_url: string;
  };
  media?: {
    desktop_media?: { url: string }[];
  };
  description?: any[];
}

interface JournalSectionProps {
  data: Data;
}

// Converter function to transform Article -> Journal
function articleToJournal(article: Article): Journal {
  const journalId = article.id ?? article.documentId ?? article.slug ?? article.name ?? 'journal';

  return {
    id: journalId,
    name: article.name ?? 'Untitled',
    awards_text: article.awards_text,
    date: article.date,
    button: {
      button_text: article.button?.button_text ?? '',
      button_url: article.button?.button_url ?? '',
    },
    media: article.media ?? {},
    description: article.description ?? [],
  };
}

function getArticleKey(article: Article, index: number): string {
  return article.id ?? article.documentId ?? article.slug ?? `${article.name ?? 'article'}-${index}`;
}

const ComponentSectionsFeaturedNews = ({ data }: JournalSectionProps) => {
  const articles = (data.journals ?? [])
    .sort((a, b) => new Date(b.date || '').getTime() - new Date(a.date || '').getTime())
    .slice(0, 3);
  const [mousePos, setMousePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [selectedJournal, setSelectedJournal] = useState<Article | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [top, setTop] = useState('');
  const [bottom, setBottom] = useState('');
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };
  useEffect(() => {
    const updatePadding = () => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        setTop(data.padding?.mobile_top_padding ?? '');
        setBottom(data.padding?.mobile_bottom_padding ?? '');
      } else {
        setTop(data.padding?.desktop_top_padding ?? '');
        setBottom(data.padding?.desktop_bottom_padding ?? '');
      }
    };

    updatePadding(); // Run on mount

    window.addEventListener('resize', updatePadding); // Listen on resize
    return () => window.removeEventListener('resize', updatePadding); // Clean up
  }, [data]);
  return (
    <section
      id={data?.__typename}
      className="pt-9 lg:pt-24 bg-[#b5c380] relative overflow-hidden"
      style={{
        paddingTop: `${top}rem`,
        paddingBottom: `${bottom}rem`,
      }}
    >
      <div className="container-main mx-auto">
        <h2 className="text-xl md:text-[26px] px-4 text-start font-light text-[#2a3329] mb-10 xl:mb-20 md:text-center">
          {data.heading}
        </h2>

        <div className="md:grid flex overflow-x-auto scroll-smooth md:overflow-x-visible md:grid-cols-3 three-blocks pb-10 lg:pb-0">
          {articles.map((article, index) => {
            const muxPlaybackId = article.media?.mux_video?.playback_id;
            const enableMuxVideo = article.media?.enable_mux_video;
            return (
              <div
                key={getArticleKey(article, index)}
                className="max-w-80 md:max-w-full min-w-80 md:min-w-min relative md:static "
                onClick={() => setSelectedJournal(article)}
              >
                <div className="px-4 xl:px-20 lg:pb-24">
                  <div className="flex justify-between items-center mb-1 font-dm-mono">
                    <Tag className="text-[#2a3329]">{article.awards_text}</Tag>
                    <Tag className="text-[#2a3329]">{article.date}</Tag>
                  </div>

                  <div
                    className="mb-4 relative img-box bg-[#B6C380] aspect-[3/4] group cursor-pointer"
                    onMouseMove={handleMouseMove}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <div
                      className="inn"
                    >
                      {enableMuxVideo && muxPlaybackId ? (
                        <MuxPlayer
                          playbackId={muxPlaybackId}
                          autoPlay
                          muted
                          loop
                          playsInline
                          streamType="on-demand"
                          style={{ width: '100%' }}
                        />
                      ) : (
                        <Image
                          src={article.media?.desktop_media?.[0]?.url ?? '/images/fallback.jpg'}
                          alt="Journal article"
                          width={471}
                          height={343}
                          className="w-full h-100 object-cover transition-all !aspect-[3/4]"
                          priority={index === 0}
                        />
                      )}
                    </div>
                    {hoveredIndex === index && article.button?.button_text && (
                      <button
                        onClick={() => setSelectedJournal(article)}
                        rel="noopener noreferrer"
                        className="text-[#E5E1D5] text-nowrap text-[12px] tracking-widest uppercase font-dm-mono absolute transition-opacity duration-300 flex items-center pointer-events-auto z-10"
                        style={{
                          left: `${mousePos.x}px`,
                          top: `${mousePos.y}px`,
                          transform: 'translate(-50%, -50%)',
                        }}
                      >
                        {article.button.button_text}
                        <Image
                          src="/images/img_frame_659.svg"
                          alt="Arrow"
                          width={12}
                          height={10}
                          className="ml-1"
                        />
                      </button>
                    )}
                  </div>

                  <p className="text-[18px] font-light text-[#2B332A] leading-[24px] cursor-pointer">
                    {article.name}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <AnimatePresence>
        {selectedJournal && (
          <JournalModal
            journal={articleToJournal(selectedJournal)}
            onClose={() => setSelectedJournal(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default ComponentSectionsFeaturedNews;
